import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Container, Paper, Typography } from "@mui/material";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import { newsService } from "../services/newsService";

const TopNews = ({ onLoadingChange }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    onLoadingChange?.(true);

    newsService.getAllNews()
      .then((data) => {
        if (mounted) setNews(data.slice(0, 4));
      })
      .catch(() => {
        if (mounted) setError("Failed to fetch news. Please try again later.");
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
          onLoadingChange?.(false);
        }
      });

    return () => { mounted = false; };
  }, [onLoadingChange]);

  const formatDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      return Number.isNaN(date.getTime()) ? "Date not available" : format(date, "MMMM dd, yyyy");
    } catch (dateError) {
      return "Date not available";
    }
  };

  const getFirstImage = (content = "") => {
    const imageMatch = content.match(/<img[^>]+src="([^">]+)"/);
    return imageMatch ? imageMatch[1] : null;
  };

  const getTextContent = (content = "") => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const text = tempDiv.textContent || tempDiv.innerText || "";
    return text.length > 100 ? `${text.substring(0, 100)}...` : text;
  };

  if (loading) {
    return (
      <section aria-label="Latest stories" className="bg-surface-950 px-5 py-8 sm:px-8 lg:px-10">
        <div className="mx-auto flex min-h-40 max-w-7xl items-center justify-center rounded-xl border border-line bg-surface-900">
          <CircularProgress aria-label="Loading news" size={30} sx={{ color: "#d81f2a" }} />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section aria-label="Latest stories" className="bg-surface-950 px-5 py-8 sm:px-8 lg:px-10">
        <div role="alert" className="mx-auto max-w-7xl rounded-xl border border-line bg-surface-900 p-6 text-sm text-gray-300">{error}</div>
      </section>
    );
  }

  return (
    <Box component="section" aria-label="Latest stories" sx={{ bgcolor: "#0a0a0b", py: { xs: 4, md: 5 } }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 3, display: "flex", alignItems: "end", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography variant="overline" component="p" sx={{ color: "#fca5a5", fontWeight: 700, letterSpacing: "0.16em" }}>
              STAY INFORMED
            </Typography>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, color: "#ffffff", letterSpacing: "-0.03em", mt: 0.5 }}>
              Latest stories
            </Typography>
            <Typography variant="body2" sx={{ color: "#9ca3af", mt: 0.5 }}>
              Updates and breaking news
            </Typography>
          </Box>
          <button type="button" onClick={() => navigate("/news")} className="hidden items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-surface-800 hover:text-white sm:inline-flex">
            All news <ArrowRightIcon aria-hidden="true" className="h-4 w-4" />
          </button>
        </Box>

        {news.length === 0 ? (
          <div className="rounded-xl border border-line bg-surface-900 px-5 py-8 text-center text-sm text-gray-400">No stories are available right now.</div>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2.5 }}>
            {news.map((item) => (
              <Paper
                key={item.id}
                component="article"
                elevation={0}
                onClick={() => navigate(`/news/${item.id}`)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(`/news/${item.id}`);
                  }
                }}
                tabIndex={0}
                role="link"
                aria-label={`Read story: ${item.headline}`}
                sx={{
                  minWidth: 0,
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: "#161618",
                  borderRadius: 2.5,
                  transition: "transform 180ms ease, border-color 180ms ease, background-color 180ms ease",
                  cursor: "pointer",
                  border: "1px solid #26262a",
                  "&:hover": { transform: "translateY(-2px)", bgcolor: "#1c1c1f", borderColor: "#3a3a40" },
                  "&:focus-visible": { outline: "2px solid #d81f2a", outlineOffset: "2px" },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {getFirstImage(item.content) && (
                    <Box sx={{ height: { xs: 160, sm: 200 }, width: "100%", borderRadius: 2, overflow: "hidden", bgcolor: "#101012" }}>
                      <img src={getFirstImage(item.content)} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </Box>
                  )}
                  <Box>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1, color: "#fff", lineHeight: 1.3, "&:hover": { color: "#fca5a5" } }}>
                      {item.headline}
                    </Typography>
                    <Typography variant="caption" component="p" sx={{ color: "#9ca3af", mb: 1 }}>
                      {formatDate(item.published_at)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", lineHeight: 1.65 }}>
                      {getTextContent(item.content)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default TopNews;
